/**
 * Entity / chip colours (spec 01-design-system §1.7). Named keys map to palette tokens; any other string is
 * treated as a CSS colour (data, e.g. a user-picked hex) and applied inline.
 */
export type ChipColor =
  | 'accent' // user accent (--ds-accent, default #0275ff)
  | 'darkBlue' // #0275ff fixed (Soat, Qurilma boshqaruvi)
  | 'blue' // #35b5ec (Chizma, GIF)
  | 'green' // #1ecd15 (Sarlavha)
  | 'darkGreen' // #509f67 (Xodimlar)
  | 'orange' // #f48200 (Video, modal-B chip)
  | 'yellow' // #f4b400 (Belgi)
  | 'pink' // #df0259 (Ko'rsatkich, Konnektorlar)
  | 'purple' // #7059ff (Rasm)
  | 'darkRed' // #e10202 (Sana)
  | 'red' // #ca3b3d (tone red)
  | 'control' // #3576ec (kit accent)
  | 'o5fcd3f'
  | 'o4a86f7'
  | 'oe4892f'
  | 'o2f8bff'
  | 'oe5558a'
  | 'o8b5cf6'
  | 'muted' // White/10 chip with a White/60 glyph (canvas KPI widget)
  | (string & {});

const CHIP_BG: Record<string, string> = {
  accent: 'bg-accent',
  darkBlue: 'bg-dark-blue-1000',
  blue: 'bg-blue-1000',
  green: 'bg-green-1000',
  darkGreen: 'bg-dark-green-1000',
  orange: 'bg-orange-1000',
  yellow: 'bg-yellow-1000',
  pink: 'bg-pink-1000',
  purple: 'bg-purple-1000',
  darkRed: 'bg-dark-red-1000',
  red: 'bg-tone-red',
  control: 'bg-control',
  o5fcd3f: 'bg-other-5fcd3f',
  o4a86f7: 'bg-other-4a86f7',
  oe4892f: 'bg-other-e4892f',
  o2f8bff: 'bg-other-2f8bff',
  oe5558a: 'bg-other-e5558a',
  o8b5cf6: 'bg-other-8b5cf6',
  muted: 'bg-field',
};

/** Named chip colours, e.g. for colour pickers in the constructor. */
export const CHIP_COLORS = Object.keys(CHIP_BG) as ChipColor[];

/** className (named colour) or inline style (raw CSS colour) for a solid chip background. */
export function chipBackground(color: ChipColor | undefined): { className?: string; style?: React.CSSProperties } {
  if (!color) return { className: CHIP_BG.accent };
  const cls = CHIP_BG[color];
  return cls ? { className: cls } : { style: { backgroundColor: color } };
}

/** Solid colours used for initial avatars in tables (497:21534), picked by a stable hash of the name. */
export const AVATAR_COLORS = ['darkBlue', 'green', 'orange', 'purple', 'pink', 'blue'] as const satisfies readonly ChipColor[];

export function colorFromString(seed: string): ChipColor {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}
